// src/services/api.js

/**
 * This file contains API service functions for backend communication.
 * It now uses the apiClient (axios instance with interceptors) from authClient.js
 * and exports a generic callApi helper.
 */
import { apiClient } from "../api/authClient";
import { createDocumentFormData } from "./apiHelpers"; // Import the helper

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Helper function to make API calls using the configured apiClient (axios).
 * Exported for use by other service files (aiService, jobService).
 */
export const callApi = async (method, endpoint, data = null, config = {}) => {
  const url = endpoint; // apiClient handles base URL

  try {
    let response;
    const axiosConfig = { ...config };

    switch (method.toUpperCase()) {
      case "GET":
        response = await apiClient.get(url, { params: data, ...axiosConfig });
        break;
      case "POST":
        response = await apiClient.post(url, data, axiosConfig);
        break;
      case "PUT":
        response = await apiClient.put(url, data, axiosConfig);
        break;
      case "DELETE":
        response = await apiClient.delete(url, axiosConfig);
        break;
      case "PATCH":
        response = await apiClient.patch(url, data, axiosConfig);
        break;
      default:
        throw new Error(`Unsupported API method: ${method}`);
    }
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      (typeof error.response?.data === "string" ? error.response.data : null) ||
      error.message ||
      "API request failed";

    console.error(
      `[callApi] API Error for ${method.toUpperCase()} ${API_BASE_URL}${
        url.startsWith("/") ? url : `/${url}`
      }:`,
      errorMessage,
      "Status:",
      error.response?.status
    );

    const errToThrow = new Error(errorMessage);
    errToThrow.status = error.response?.status;
    errToThrow.data = error.response?.data;
    errToThrow.originalError = error;
    throw errToThrow;
  }
};

// --- User Profile ---
export const getUserProfile = async () => {
  return await callApi("GET", `/profile/user-profiles/`);
};
export const updateUserProfile = async (profileData) => {
  return await callApi("PUT", `/profile/user-profiles/`, profileData);
};
export const uploadProfilePicture = async (formData) => {
  return await callApi("PUT", `/profile/user-profiles/photo`, formData);
};

// --- Experience ---
export const addExperience = async (experienceData) => {
  return await callApi("POST", `/profile/experience/`, experienceData);
};
export const getAllExperience = async () => {
  return await callApi("GET", `/profile/experience/`);
};
export const updateExperience = async (expId, experienceData) => {
  return await callApi("PUT", `/profile/experience/${expId}`, experienceData);
};
export const deleteExperience = async (expId) => {
  return await callApi("DELETE", `/profile/experience/${expId}`);
};

// --- Education ---
export const addEducationRecord = async (educationData) => {
  return await callApi("POST", `/profile/education/records/`, educationData);
};
export const getAllEducation = async () => {
  return await callApi("GET", `/profile/education/records/`);
};
export const getAllInstitutions = async () => {
  return await callApi("GET", `/profile/education/institutions/`);
};
export const createInstitution = async (institutionData) => {
  return await callApi(
    "POST",
    `/profile/education/institutions/`,
    institutionData
  );
};
export const getOrCreateInstitution = async (institutionData) => {
  if (!institutionData || !institutionData.name)
    throw new Error("Institution name is required for getOrCreateInstitution.");
  const institutionNameLower = institutionData.name.toLowerCase();
  try {
    const allInstitutions = await getAllInstitutions();
    const existing = allInstitutions.find(
      (inst) => inst.name.toLowerCase() === institutionNameLower
    );
    if (existing) return existing;
  } catch (findError) {
    console.warn(
      "[api.js getOrCreateInstitution] Error finding institutions, proceeding to create:",
      findError
    );
  }
  return await createInstitution(institutionData);
};
export const updateEducationRecord = async (recordId, educationData) => {
  return await callApi(
    "PUT",
    `/profile/education/records/${recordId}`,
    educationData
  );
};
export const deleteEducationRecord = async (recordId) => {
  return await callApi("DELETE", `/profile/education/records/${recordId}`);
};

// --- Skills ---
export const getAllSkills = async () => {
  return await callApi("GET", `/profile/skills/master/`);
};
export const createSkill = async (skillData) => {
  return await callApi("POST", `/profile/skills/master/`, skillData);
};
export const getOrCreateSkill = async (skillName, category = null) => {
  if (!skillName || !skillName.trim())
    throw new Error("Skill name is required for getOrCreateSkill.");
  const skillNameLower = skillName.trim().toLowerCase();
  const skillData = { name: skillName.trim(), category: category };
  try {
    const allSkills = await getAllSkills();
    const existing = allSkills.find(
      (skill) =>
        skill.name.toLowerCase() === skillNameLower &&
        (category === null ||
          (skill.category &&
            category &&
            skill.category.toLowerCase() === category.toLowerCase()) ||
          (!skill.category && !category))
    );
    if (existing) return existing;
  } catch (findError) {
    console.warn(
      "[api.js getOrCreateSkill] Error finding skills, proceeding to create:",
      findError
    );
  }
  return await createSkill(skillData);
};
export const addUserSkill = async (userSkillData) => {
  return await callApi("POST", `/profile/skills/user/`, userSkillData);
};
export const getUserSkills = async () => {
  return await callApi("GET", `/profile/skills/user/`);
};
export const updateUserSkill = async (userSkillId, userSkillData) => {
  return await callApi(
    "PUT",
    `/profile/skills/user/${userSkillId}`,
    userSkillData
  );
};
export const deleteUserSkill = async (userSkillId) => {
  return await callApi("DELETE", `/profile/skills/user/${userSkillId}`);
};

// --- Certifications ---
export const getAllCertificateDefinitions = async () => {
  return await callApi("GET", `/profile/certifications/definitions/`);
};
export const createCertificateDefinition = async (definitionData) => {
  return await callApi(
    "POST",
    `/profile/certifications/definitions/`,
    definitionData
  );
};
export const getOrCreateCertificateDefinition = async (definitionName) => {
  if (!definitionName || !definitionName.trim())
    throw new Error("Certificate definition name is required.");
  const nameLower = definitionName.trim().toLowerCase();
  const definitionData = { name: definitionName.trim() };
  try {
    const allDefinitions = await getAllCertificateDefinitions();
    const existing = allDefinitions.find(
      (def) => def.name.toLowerCase() === nameLower
    );
    if (existing) return existing;
  } catch (findError) {
    console.warn(
      "[api.js getOrCreateCertificateDefinition] Error finding cert definitions, proceeding to create:",
      findError
    );
  }
  return await createCertificateDefinition(definitionData);
};
export const addUserCertification = async (userCertificationData) => {
  return await callApi(
    "POST",
    `/profile/certifications/user/`,
    userCertificationData
  );
};
export const getUserCertifications = async () => {
  return await callApi("GET", `/profile/certifications/user/`);
};

// --- THIS IS THE MISSING FUNCTION ---
export const deleteUserCertification = async (userCertificationId) => {
  return await callApi(
    "DELETE",
    `/profile/certifications/user/${userCertificationId}`
  );
};

// --- Documents (Resumes, etc.) ---
export const uploadDocument = async (formData) => {
  return await callApi("POST", `/profile/resumes/`, formData);
};
export const getUserDocuments = async () => {
  return await callApi("GET", `/profile/resumes/`);
};
export const deleteDocument = async (resumeId) => {
  return await callApi("DELETE", `/profile/resumes/${resumeId}`);
};
export const setPrimaryDocument = async (resumeId) => {
  return await callApi("PUT", `/profile/resumes/${resumeId}/set-primary`);
};
export const getResumeContent = async (resumeId) => {
  if (!resumeId) throw new Error("No resume ID provided.");
  try {
    const response = await apiClient.get(
      `/profile/resumes/${resumeId}/content`,
      { responseType: "text" }
    );
    return response.data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.detail ||
      error.message ||
      `Failed to load content for resume ${resumeId}.`;
    throw new Error(errorMessage);
  }
};

// --- AI / Prompt Endpoints ---
export const getAIJobCategories = async (formData) => {
  return await callApi("POST", "/prompt/job-categories", formData);
};
export const fillProfileFromResume = async (resumeId) => {
  if (!resumeId) {
    throw new Error("A valid resume ID is required to fill the profile.");
  }
  return await callApi(
    "POST",
    `/profile/resumes/${resumeId}/parse-and-fill-profile`
  );
};

export const uploadAndParseResume = async (resumeFile) => {
  const resumeFormData = createDocumentFormData(
    resumeFile,
    `AI-Upload-${resumeFile.name}`,
    "resume",
    true
  );
  const uploadResponse = await uploadDocument(resumeFormData);
  if (!uploadResponse || !uploadResponse.id) {
    throw new Error("Failed to upload resume or did not receive a resume ID.");
  }
  const resumeId = uploadResponse.id;
  return await fillProfileFromResume(resumeId);
};
